import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { findGroup, groupPath } from '../lib/groupPath';
import { formatJoinCode } from '../lib/joinCode';
import { useStore } from '../store/useStore';

type Mode = 'login' | 'register';

/**
 * Sign-in and sign-up, one form. `/login` and `/signup` decide which it opens
 * as; an invite or join code arriving in the URL overrides that, since somebody
 * following such a link almost always needs an account first.
 */
export function Login({
  mode: initialMode = 'login',
  inviteToken,
  joinCode,
}: {
  mode?: Mode;
  inviteToken?: string;
  joinCode?: string;
}) {
  const arrivingWithLink = Boolean(inviteToken ?? joinCode);

  const [mode, setMode] = useState<Mode>(arrivingWithLink ? 'register' : initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const login = useStore((s) => s.login);
  const register = useStore((s) => s.register);
  const navigate = useNavigate();

  function switchMode() {
    const next: Mode = mode === 'login' ? 'register' : 'login';
    setMode(next);
    setError(null);
    // Keep the URL honest — except on an invite or join link, where leaving the
    // route would drop the token the page is holding.
    if (!arrivingWithLink) navigate(next === 'login' ? '/login' : '/signup', { replace: true });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        const invitedGroupId = await register({ email, password, displayName, inviteToken });
        if (inviteToken && invitedGroupId) {
          const group = findGroup(useStore.getState().groups, invitedGroupId);
          navigate(group ? groupPath(group) : '/', { replace: true });
        }
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-10">
      <div className="animate-rise w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <Link to="/" aria-label="Project-Workflow home">
            <Logo large />
          </Link>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Project-Workflow</h1>
            <p className="mt-1 text-sm text-ink-soft">
              {mode === 'login' ? 'Sign in to your team workspace.' : 'Create your account.'}
            </p>
          </div>
        </div>

        {joinCode && (
          <p className="mb-4 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2.5 text-center text-sm text-ink-soft">
            You're joining a team with code{' '}
            <span className="font-mono text-ink">{formatJoinCode(joinCode)}</span>. Sign in or create
            an account to continue.
          </p>
        )}

        {inviteToken && (
          <p className="mb-4 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2.5 text-center text-sm text-ink-soft">
            You've been invited to a team. Create your account with the email the invite was sent to.
          </p>
        )}

        <form onSubmit={submit} className="card flex flex-col gap-4 p-5">
          {mode === 'register' && (
            <Labelled label="Display name">
              <input
                className="field-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                maxLength={80}
                autoComplete="name"
                placeholder="How your teammates see you"
              />
            </Labelled>
          )}

          <Labelled label="Email">
            <input
              className="field-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          </Labelled>

          <Labelled label="Password" hint={mode === 'register' ? 'At least 10 characters' : undefined}>
            <input
              className="field-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === 'register' ? 10 : undefined}
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              placeholder="••••••••••"
            />
          </Labelled>

          {error && (
            <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>

          <button
            type="button"
            onClick={switchMode}
            className="text-xs text-ink-faint transition-colors hover:text-ink-soft"
          >
            {mode === 'login'
              ? "Don't have an account? Create one"
              : 'Already have an account? Sign in'}
          </button>
        </form>

        {mode === 'register' && !arrivingWithLink && (
          <p className="mt-4 text-center text-xs leading-relaxed text-ink-faint">
            After signing up you can create your own team, or join an existing one with a join code
            or invite link.
          </p>
        )}

        <Link
          to="/"
          className="mt-5 block text-center text-xs text-ink-faint transition-colors hover:text-ink-soft"
        >
          ← What is Project-Workflow?
        </Link>
      </div>
    </div>
  );
}

function Labelled({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between">
        <span className="text-xs font-medium text-ink-soft">{label}</span>
        {hint && <span className="text-[11px] text-ink-faint">{hint}</span>}
      </span>
      {children}
    </label>
  );
}
